/**
 * The academic director's vetting API: every request and response under
 * `/api/vet`, and the admin routes that make a vetted topic available to
 * students. The `/vet` pages build from these types alone.
 *
 * Every `/api/vet` route is gated by `content:vet` and then by the caller's
 * vetting scope (`shared/vetting/scope.ts`): a topic outside it answers 404
 * like a topic that does not exist. Release is gated by `content:release`.
 *
 * Wording rule: every string in a response is something a director may read,
 * so it is plain (founder, 4 November: Google Docs is the baseline). Labels
 * name things by their source and title ("Worksheet 2 · Question 4", "Notes ·
 * Isotopes", "Practice variations · Metal or non-metal"), never by a page
 * number, and no response text says family, cases, fingerprint, spec, regex,
 * pack or release. Field names are for the UI, not the director.
 *
 * Errors are `{ message: string; code?: string }` with a plain message
 * ("Couldn't save your decision. Reload the page and try again.").
 */
import type { ServedNotesExcerpt } from '../chemistry/api.js';
import type { Blocks, NotesTile } from '../chemistry/pack.js';
import type { ElectronShellBuilderState } from '../interactive/electronShellBuilder.js';
import type { NuclideBuilderState } from '../interactive/nuclideBuilder.js';
import type { StudentResponseSpec } from '../schemas/responseSpec.js';
import type { VettingScope } from './scope.js';

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

const seg = (value: string) => encodeURIComponent(value);

/** API paths, relative to `/api` like `CHEMISTRY_PRACTICE_PATHS`. */
export const VET_API_PATHS = {
  /** `GET` → {@link VetQueueHome} */
  queue: '/vet/queue',
  /** `GET` → {@link VetItemPayload} */
  item: (packId: string, itemId: string) =>
    `/vet/${seg(packId)}/items/${seg(itemId)}`,
  /** `GET` → {@link VetSectionPayload} */
  section: (packId: string, sectionId: string) =>
    `/vet/${seg(packId)}/sections/${seg(sectionId)}`,
  /** `GET ?page=0` → {@link VetVariationsPayload}; each page is three questions. */
  variations: (packId: string, familyId: string, page = 0) =>
    `/vet/${seg(packId)}/variations/${seg(familyId)}?page=${page}`,
  /** `GET` → {@link VetRecentResponse} (the last twenty decisions) */
  recent: '/vet/recent',
  /** `POST` {@link VetDecideRequest} → {@link VetDecideResponse} */
  decisions: '/vet/decisions',
  /** `POST` {@link VetUndoRequest} → {@link VetUndoResponse} */
  undo: '/vet/decisions/undo',
  /** `POST` (no body) → {@link VetIntroSeenResponse} */
  introSeen: '/vet/intro-seen',
  /** `GET ?packId&kind&targetId` → {@link VetCommentListResponse}; `POST` {@link VetCommentCreateRequest} → {@link VetCommentResponse} */
  comments: '/vet/comments',
  /** `PATCH` {@link VetCommentEditRequest} → {@link VetCommentResponse}; `DELETE` → 204 */
  comment: (commentId: string) => `/vet/comments/${seg(commentId)}`,
  /** `POST` {@link VetCommentReplyRequest} → {@link VetCommentResponse} */
  commentReplies: (commentId: string) =>
    `/vet/comments/${seg(commentId)}/replies`,
  /** `PATCH` {@link VetCommentEditRequest} → {@link VetCommentResponse}; `DELETE` → {@link VetCommentResponse} */
  commentReply: (commentId: string, replyId: string) =>
    `/vet/comments/${seg(commentId)}/replies/${seg(replyId)}`,
  /** `POST` {@link VetCommentResolveRequest} → {@link VetCommentResponse} */
  commentResolve: (commentId: string) =>
    `/vet/comments/${seg(commentId)}/resolve`,
  /** `GET ?packId&itemId` → {@link VetWordingListResponse}; `POST` {@link VetWordingCreateRequest} → {@link VetWordingCreateResponse} */
  wordings: '/vet/wordings',
  /** `GET` → {@link VetFlagListResponse} */
  flags: '/vet/flags',
  /** `GET` → {@link VetFlagPayload} */
  flag: (flagId: string) => `/vet/flags/${seg(flagId)}`,
  /**
   * `GET`, signed out → {@link VetAccessCodeSummary}; 404 for an unknown,
   * inactive, expired or used-up code, or one that is not a director's.
   */
  accessCodeSummary: (code: string) => `/vet/access-code/${seg(code)}/summary`,
  /** `POST` {@link VetFlagAnswerRequest} → {@link VetFlagAnswerResponse} */
  flagAnswer: (flagId: string) => `/vet/flags/${seg(flagId)}/answer`,
} as const;

/** Admin paths, relative to `/api`, gated by `content:release`. */
export const CONTENT_RELEASE_PATHS = {
  /** `GET` → {@link ContentReleaseListResponse} */
  list: '/admin/chemistry-packs',
  /** `POST` {@link ContentReleaseRequest} → {@link ContentReleaseResponse}; 409 {@link ContentReleaseRefusal} */
  release: (packId: string) => `/admin/chemistry-packs/${seg(packId)}/release`,
  /** `POST` (no body) → {@link ContentReleaseResponse} */
  withdraw: (packId: string) =>
    `/admin/chemistry-packs/${seg(packId)}/withdraw`,
} as const;

/** The `/vet` pages; each is a `ROUTE_ACCESS` row on `content:vet`. */
export const VET_PAGE_PATHS = {
  home: '/vet',
  flags: '/vet/flags',
  recent: '/vet/recent',
  flag: (flagId: string) => `/vet/flags/${seg(flagId)}`,
  target: (target: VetTargetRef) =>
    `/vet/${seg(target.packId)}/${target.kind}/${seg(target.id)}`,
} as const;

// ---------------------------------------------------------------------------
// Targets and their state
// ---------------------------------------------------------------------------

/** What a director decides on. A flag is answered, not decided (see flags below). */
export const VET_TARGET_KINDS = ['item', 'section', 'drill_family'] as const;
export type VetTargetKind = (typeof VET_TARGET_KINDS)[number];

/** One item, notes section or set of practice variations in one topic. */
export interface VetTargetRef {
  packId: string;
  kind: VetTargetKind;
  /** Item id, notes section id or drill family id. */
  id: string;
}

/**
 * Where a target stands for the director. `back_to_you`: decided before, then
 * the content changed (a change the director asked for, or one after a
 * teacher's flag); it waits for "Looks right" or "Needs change".
 */
export type VetState = 'to_vet' | 'vetted' | 'needs_change' | 'back_to_you';

/** A person, named in text (no avatars, C23). */
export interface VetPerson {
  name: string;
  /** The signed-in director: the UI says "you". */
  you: boolean;
}

/**
 * Where a change sits, so the UI can highlight it in place: a part (and its
 * prompt, model answer, working or marking), a marking point with the
 * wordings added or taken away, an option's reason, a notes tile, or the
 * rule of a set of practice variations.
 */
export interface VetChangedSpot {
  partId?: string;
  pointId?: string;
  optionId?: string;
  tileIndex?: number;
  field: VetCommentField;
  /** Wordings that now get the mark ("A student also gets this mark for writing any of these"). */
  addedWordings?: string[];
  /** Wordings that no longer get the mark. */
  removedWordings?: string[];
  /**
   * Where the change replaced text (a prompt, a model answer, the working, a
   * marking point's idea, an option or its reason, a notes tile, the rule):
   * the text before and after, plain with `$…$` maths, so the diff can show
   * both. Absent when no text changed (a figure, how a part is marked).
   */
  before?: string;
  after?: string;
}

export interface VetBackToYou {
  /** One plain sentence: "We widened the accepted wordings of part (c) after a teacher's flag." */
  reason: string;
  /** "Content team". */
  by: string;
  /** When the change was made, when known. */
  at: string | null;
  /** When the director decided before the change. */
  decidedAt: string | null;
  /** What the decision was before the change. */
  previous: 'vetted' | 'needs_change';
  changes: VetChangedSpot[];
}

export interface VetReviewState {
  state: VetState;
  decidedBy: VetPerson | null;
  decidedAt: string | null;
  /** The note sent with "Needs change". */
  note: string | null;
  backToYou: VetBackToYou | null;
  /**
   * The content version this view shows. Sent back with a decision: if the
   * content changed meanwhile the server answers 409 and the page reloads,
   * so nothing is ever vetted unseen.
   */
  contentVersion: string;
  /** Sent back with a decision so two tabs cannot overwrite each other. */
  reviewRevision: number;
}

// ---------------------------------------------------------------------------
// Queue home
// ---------------------------------------------------------------------------

/** `34 of 44 vetted · 2 need changes · 8 to vet`; to vet includes back to you. */
export interface VetCounts {
  total: number;
  vetted: number;
  needChanges: number;
  toVet: number;
}

export interface VetTopicCard {
  packId: string;
  topic: { id: string; name: string; order: number };
  items: VetCounts;
  sections: VetCounts;
  variations: VetCounts;
  /** `Start vetting`: the first thing still waiting for a decision, in queue order. */
  start: VetTargetRef | null;
  /** Every item, notes section and set of practice variations is vetted. */
  allVetted: boolean;
  /** Students can see this topic now. */
  availableToStudents: boolean;
}

export type VetBackToYouRow =
  | {
      kind: 'changed';
      target: VetTargetRef;
      label: string;
      topicName: string;
      /** The plain reason, e.g. "Accepted wordings widened after a teacher's flag". */
      reason: string;
      at: string | null;
    }
  | {
      kind: 'flag';
      flagId: string;
      target: VetTargetRef | null;
      label: string;
      topicName: string;
      /** "Flag from Ms Lee · Answer or solution is wrong". */
      reason: string;
      at: string;
    };

/** `GET /api/vet/queue` */
export interface VetQueueHome {
  /** "Chemistry · Sec 3 G3 Pure". */
  heading: string;
  /** False until the director closes the three-step card once. */
  introSeen: boolean;
  /** Changed since decided, and flags waiting for an answer, newest first. */
  backToYou: VetBackToYouRow[];
  /** In the teaching order. */
  topics: VetTopicCard[];
  /** The last twenty decisions, newest first; the newest can be undone. */
  recent: VetRecentDecision[];
  /** Flags waiting for an answer: the Flags count in the side bar. */
  openFlags: number;
}

/** `GET /api/vet/recent` */
export interface VetRecentResponse {
  recent: VetRecentDecision[];
}

/** One past decision in the Recent rail and in "Just before this". */
export interface VetRecentDecision {
  /** Pass to undo. */
  decisionId: string;
  /** What was decided on; a flag answer carries `flagId` and its item. */
  target: VetTargetRef | null;
  flagId: string | null;
  /** "Worksheet 1 · Question 2". */
  label: string;
  topicName: string;
  decision: 'vetted' | 'needs_change' | 'looks_right' | 'vetted_as_is';
  at: string;
  /** Only the newest decision can be undone; it stays undoable with no timer. */
  canUndo: boolean;
}

// ---------------------------------------------------------------------------
// One item, as students see it with the answers beside it
// ---------------------------------------------------------------------------

/** A part as students see it: the inputs render inert ("You don't need to answer"). */
export interface VetPartView {
  id: string;
  /** "(a)", "(b)(i)". */
  label: string;
  prompt: Blocks;
  marks: number;
  /** The answer-free input students get; drives "Try it as a student". */
  response: StudentResponseSpec;
  /** The notes section the prompt refers to, drawn collapsed under it. */
  notesExcerpt?: ServedNotesExcerpt;
  /**
   * For a part students answer in a builder (`response.type ===
   * 'interactive'`): the builder's state holding the model answer, so the
   * inert builder in the student column draws it. The shape is the module's
   * own state: the electron-shell builder's `{ shells }`, the nuclide
   * builder's `{ A, Z, symbol, charge }` as typed strings.
   */
  modelState?: VetBuilderModelState;
}

/** The model answer as an interactive builder's state. */
export type VetBuilderModelState =
  | ElectronShellBuilderState
  | NuclideBuilderState;

/**
 * A wording that gets a mark. `addedBy` is set when a director added it with
 * "Add a wording that should count" ("Added by you"); `inPlace` once the
 * content team has put it into the question, which never sends the item back.
 */
export interface VetWording {
  text: string;
  addedBy?: VetPerson & { at: string; inPlace: boolean; wordingId: string };
}

/** "A student gets this mark for writing any of these:" / "Not for:". */
export interface VetMarkingPoint {
  id: string;
  /** The idea as the model answer states it. */
  text: string;
  marks: number;
  accepted: VetWording[];
  notFor: string[];
}

/** One answer box inside a part with several (protons, electrons, neutrons). */
export interface VetFieldMarking {
  id: string;
  label: string;
  /** Interchangeable with the other boxes of its group, in any order. */
  anyOrder: boolean;
  marking: Extract<
    VetPartMarking,
    { type: 'words' | 'number' | 'configuration' }
  >;
}

/** How a part is marked, in plain terms. Pattern strings never appear. */
export type VetPartMarking =
  | {
      type: 'choice';
      /** The correct option's id ("D"). */
      answer: string;
      /** Each option's reason, shown to a student after they choose. */
      reasons: Array<{ optionId: string; reason: string | null }>;
      /** "Options keep this order for every student." */
      fixedOrder: boolean;
    }
  | {
      type: 'choices';
      answers: string[];
      reasons: Array<{ optionId: string; reason: string | null }>;
    }
  | { type: 'words'; accepted: VetWording[] }
  | {
      type: 'number';
      /** "24.3". */
      answer: string;
      unit: string | null;
      sigFigs: number | null;
      /** Any answer from `from` to `to` gets the mark. */
      range: { from: string; to: string };
      /** "Stopping at 24.27, before rounding, gets part of the mark." */
      partWay: string | null;
    }
  | { type: 'configuration'; answer: string }
  | {
      type: 'nuclide';
      answer: { A: number; Z: number; symbol: string; charge: number | null };
    }
  | { type: 'fields'; fields: VetFieldMarking[] }
  | {
      type: 'blanks';
      blanks: Array<{ id: string; accepted: VetWording[]; anyOrder: boolean }>;
    }
  | { type: 'points'; points: VetMarkingPoint[] }
  /** A drawing students build (shells, a nuclide); `answer` reads it out: "2, 8, 3". */
  | { type: 'drawing'; answer: string }
  /** No automatic mark: "The teacher marks this part." */
  | { type: 'teacher' };

/** The answer column beside a part. */
export interface VetPartAnswer {
  partId: string;
  label: string;
  marks: number;
  /** The model answer as the director wrote it. */
  model?: Blocks;
  working?: Blocks;
  /** The notes section the working refers to, drawn open under it. */
  notesExcerpt?: ServedNotesExcerpt;
  marking: VetPartMarking;
}

interface VetTargetPayloadBase {
  target: VetTargetRef;
  /** "Worksheet 2 · Question 8", "Notes · Inside atoms", "Practice variations · Metal or non-metal". */
  label: string;
  topic: { id: string; name: string };
  /** "Item 35 of 44", "1 of 5 sections", "1 of 8". */
  position: { index: number; count: number };
  review: VetReviewState;
  comments: VetComment[];
  /** The next thing waiting for a decision after this one, in queue order. */
  next: VetTargetRef | null;
  /** "Just before this": the director's newest decision, with Undo. */
  lastDecision: VetRecentDecision | null;
  /**
   * Where the topic's scanned figures (`source_image` files) load from: the
   * same signed, expiring path the student payloads carry (prefix the API
   * base URL, then append the figure's `file`). Absent when the server cannot
   * sign media links.
   */
  mediaBaseUrl?: string;
}

/** `GET /api/vet/:packId/items/:itemId` */
export interface VetItemPayload extends VetTargetPayloadBase {
  /** "Multiple choice", "Written question", "Checkpoint question", "Practice question". */
  kindLabel: string;
  /** Written questions name their context: "Helium in volcanic rock". */
  title: string | null;
  marks: number;
  context?: Blocks;
  parts: VetPartView[];
  answers: VetPartAnswer[];
  /** The whole-question solution, shown to students after answering. */
  solution?: Blocks;
  /** Flags teachers raised on this question that wait for an answer. */
  openFlags: VetFlagSummary[];
  /** The skill the question practises (its primary skill). */
  skill: { id: string; name: string };
}

/** One notes section in the cover's Contents list. */
export interface VetContentsEntry {
  id: string;
  title: string;
  /** Where the section stands for the director. */
  state: VetState;
  /** Vetted or sent for a change: the Contents list ticks it. */
  decided: boolean;
  /** The section this page shows. */
  current: boolean;
}

/** `GET /api/vet/:packId/sections/:sectionId`: the tile feed with the blanks filled. */
export interface VetSectionPayload extends VetTargetPayloadBase {
  title: string;
  /** Cloze tiles carry their answers; the UI draws each blank filled with it. */
  tiles: NotesTile[];
  /**
   * Every notes section of the topic in reading order, for the cover's
   * Contents list and "2 of 5 sections" (as the student feed's cover shows).
   */
  sections: VetContentsEntry[];
  /** This section's place in `sections`, from 0. */
  currentIndex: number;
}

/** One sample question made from the rule, with its answer and working. */
export interface VetVariationSample {
  /** Stable across pages; comments on a sample anchor to it. */
  sampleIndex: number;
  context?: Blocks;
  part: VetPartView;
  answer: VetPartAnswer;
}

/** `GET /api/vet/:packId/variations/:familyId?page=N` */
export interface VetVariationsPayload extends VetTargetPayloadBase {
  title: string;
  /** "DeepThink makes these questions from this rule:" + this sentence. */
  rule: string;
  /** The skill the questions practise, in words. */
  skillName: string;
  /** How many questions the rule makes ("30 questions"). */
  questionCount: number;
  /** Three per page; "See three more" asks for the next page. */
  samples: VetVariationSample[];
  page: { index: number; size: number; hasMore: boolean };
}

// ---------------------------------------------------------------------------
// The access code entry page, before Google sign-in
// ---------------------------------------------------------------------------

/**
 * `GET /api/vet/access-code/:code/summary`, signed out and rate-limited: what
 * a director's code grants, read out before sign-in. Only the sentence and
 * the scope; never the code's team, uses or expected email.
 */
export interface VetAccessCodeSummary {
  /** "This code makes you the Academic director for Chemistry, covering Sec 3 G3 and Sec 4 G3, Pure." */
  sentence: string;
  /** The words the page sets in bold: "Academic director for Chemistry". */
  emphasis: string;
  scope: VettingScope;
}

// ---------------------------------------------------------------------------
// Decisions
// ---------------------------------------------------------------------------

/**
 * `vetted` and `needs_change` on anything waiting; `looks_right` on
 * something back to you, which vets the changed content. `needs_change`
 * requires a note. A decision is always a click: no keyboard shortcuts (C31).
 */
export type VetDecision = 'vetted' | 'needs_change' | 'looks_right';

/** `POST /api/vet/decisions` */
export interface VetDecideRequest {
  target: VetTargetRef;
  decision: VetDecision;
  /** Required with `needs_change`, at most 4000 characters. */
  note?: string;
  contentVersion: string;
  reviewRevision: number;
}

export interface VetDecideResponse {
  review: VetReviewState;
  /** The decision just made, for "Saved" and the Recent rail. */
  decision: VetRecentDecision;
  next: VetTargetRef | null;
}

/** `POST /api/vet/decisions/undo`: undoes the director's newest decision. */
export interface VetUndoRequest {
  /** The newest decision's id, so the undo is the one the director sees. */
  decisionId: string;
}

export interface VetUndoResponse {
  /** What the undo reopened. */
  target: VetTargetRef | null;
  flagId: string | null;
  /** The target's state after the undo (null for a flag answer). */
  review: VetReviewState | null;
  /** The decision now newest, if any. */
  lastDecision: VetRecentDecision | null;
}

/** `POST /api/vet/intro-seen` */
export interface VetIntroSeenResponse {
  introSeen: true;
}

// ---------------------------------------------------------------------------
// Comments: Docs words (Comment, Reply, Resolve, Edit, Delete)
// ---------------------------------------------------------------------------

export const VET_COMMENT_FIELDS = [
  'question',
  'context',
  'prompt',
  'model',
  'working',
  'marking',
  'reason',
  'solution',
  'tile',
  'rule',
  'sample',
] as const;
export type VetCommentField = (typeof VET_COMMENT_FIELDS)[number];

/**
 * The exact thing a comment sits on: a part, one of its marking points or an
 * option's reason, a notes tile, the rule or one sample question. An empty
 * anchor sits on the whole thing.
 */
export interface VetCommentAnchor {
  field?: VetCommentField;
  partId?: string;
  pointId?: string;
  optionId?: string;
  tileIndex?: number;
  sampleIndex?: number;
}

export interface VetCommentReply {
  id: string;
  body: string;
  author: VetPerson;
  at: string;
  editedAt: string | null;
}

export interface VetComment {
  id: string;
  target: VetTargetRef;
  anchor: VetCommentAnchor;
  /** The words the director selected, quoted as Docs does. */
  quote: string | null;
  body: string;
  author: VetPerson;
  at: string;
  editedAt: string | null;
  resolved: { by: VetPerson; at: string } | null;
  replies: VetCommentReply[];
}

/** `POST /api/vet/comments`; body and quote at most 4000 characters. */
export interface VetCommentCreateRequest {
  target: VetTargetRef;
  anchor: VetCommentAnchor;
  quote?: string;
  body: string;
}

/** `PATCH /api/vet/comments/:id` and `…/replies/:replyId`: only the author edits. */
export interface VetCommentEditRequest {
  body: string;
}

/** `POST /api/vet/comments/:id/replies` */
export interface VetCommentReplyRequest {
  body: string;
}

/** `POST /api/vet/comments/:id/resolve`; `false` reopens. */
export interface VetCommentResolveRequest {
  resolved: boolean;
}

export interface VetCommentResponse {
  comment: VetComment;
}

/** `GET /api/vet/comments?packId&kind&targetId`, unresolved first. */
export interface VetCommentListResponse {
  comments: VetComment[];
}

// ---------------------------------------------------------------------------
// "Add a wording that should count"
// ---------------------------------------------------------------------------

/**
 * `POST /api/vet/wordings`: a phrase that should get the mark. The content
 * team puts it into the question; that change never sends the question back
 * to the director, and it never changes the director's decision.
 */
export interface VetWordingCreateRequest {
  packId: string;
  itemId: string;
  partId: string;
  /**
   * The marking point, answer box or blank the wording is for; null for a
   * part with one short answer.
   */
  pointId: string | null;
  /** At most 200 characters. */
  wording: string;
}

export interface VetAddedWording {
  id: string;
  packId: string;
  itemId: string;
  partId: string;
  pointId: string | null;
  wording: string;
  by: VetPerson;
  at: string;
  /** The content team has put it into the question. */
  inPlace: boolean;
}

export interface VetWordingCreateResponse {
  wording: VetAddedWording;
}

/** `GET /api/vet/wordings?packId&itemId` (itemId optional). */
export interface VetWordingListResponse {
  wordings: VetAddedWording[];
}

// ---------------------------------------------------------------------------
// Flags from teachers
// ---------------------------------------------------------------------------

/**
 * `vetted_as_is` keeps the question and sends the teacher the note (she sees
 * it beside the question); `needs_change` sends the question to the content
 * team with the note, and the question stays out of a topic's opening until
 * it is fixed and vetted.
 */
export type VetFlagAnswer = 'vetted_as_is' | 'needs_change';

export interface VetFlagSummary {
  flagId: string;
  /** "Ms Lee", "Teacher". */
  from: { name: string; role: string };
  /** "Homework", "Lesson prep". */
  where: string;
  /** "Sec 3 G3". */
  levelLabel: string | null;
  /** "Answer or solution is wrong". */
  reason: string;
  /** The teacher's own words, quoted. */
  words: string | null;
  at: string;
  /** The question flagged; null when it is no longer in the topic. */
  target: VetTargetRef | null;
  label: string;
  topicName: string;
  answer: {
    decision: VetFlagAnswer;
    note: string;
    by: VetPerson;
    at: string;
  } | null;
}

/** `GET /api/vet/flags` */
export interface VetFlagListResponse {
  waiting: VetFlagSummary[];
  answered: VetFlagSummary[];
}

/** `GET /api/vet/flags/:flagId` */
export interface VetFlagPayload {
  flag: VetFlagSummary;
  /** The question as students see it with its answers; null when it is gone. */
  item: VetItemPayload | null;
}

/** `POST /api/vet/flags/:flagId/answer`; the note is required, at most 1000 characters. */
export interface VetFlagAnswerRequest {
  decision: VetFlagAnswer;
  note: string;
}

export interface VetFlagAnswerResponse {
  flag: VetFlagSummary;
  decision: VetRecentDecision;
}

// ---------------------------------------------------------------------------
// Admin: making a vetted topic available to students
// ---------------------------------------------------------------------------

/** Something still waiting before a topic can open to students. */
export interface ContentReleaseWaiting {
  target: VetTargetRef;
  label: string;
  state: Exclude<VetState, 'vetted'>;
}

export interface ContentReleaseStatus {
  packId: string;
  topic: { id: string; name: string; order: number };
  availableToStudents: boolean;
  releasedAt: string | null;
  /** Every item, notes section and set of practice variations is vetted. */
  ready: boolean;
  waiting: ContentReleaseWaiting[];
}

/** `GET /api/admin/chemistry-packs` */
export interface ContentReleaseListResponse {
  topics: ContentReleaseStatus[];
}

/** `POST /api/admin/chemistry-packs/:packId/release` */
export interface ContentReleaseRequest {
  note?: string;
}

export interface ContentReleaseResponse {
  status: ContentReleaseStatus;
}

/** 409 from release while anything in the topic is not vetted. */
export interface ContentReleaseRefusal {
  message: string;
  code: 'not_all_vetted';
  waiting: ContentReleaseWaiting[];
}
